import { types, applySnapshot } from 'mobx-state-tree';
import { toJS } from 'mobx';
import Framework from '@greenville/framework';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';
import { buildRowScopedExportPayload } from '../common/utils/auditExportColumnUtils';

const chartsData = types.model('chartsData', {
  identifierId: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  type: types.maybeNull(types.string),
  usercommand: types.maybeNull(types.string),
  response: types.maybeNull(types.string),
  violenceCategory: types.maybeNull(types.string),
  violenceSeverity: types.maybeNull(types.number),
  userId: types.maybeNull(types.string),
  requestId: types.maybeNull(types.string),
  hateCategory: types.maybeNull(types.string),
  hateSeverity: types.maybeNull(types.number),
  selfharmCategory: types.maybeNull(types.string),
  selfharmSeverity: types.maybeNull(types.number),
  sexualCategory: types.maybeNull(types.string),
  sexualSeverity: types.maybeNull(types.number),
  blockedWords: types.optional(types.array(types.string), [])
});

const TrendsBlockedChartsDetailsModel = types
  .model('TrendsBlockedChartsDetailsModel', {
    status: types.maybeNull(types.string),
    data: types.optional(types.array(chartsData), []),
    totalCount: types.optional(types.number, 0),
    totalPages: types.optional(types.number, 0),
    currentPage: types.optional(types.number, 0),
    pageSize: types.optional(types.number, constants.PAGINATION.LIMIT),
    lastPayload: types.optional(types.frozen(), {})
  })
  .views((self) => ({
    get blockedChartsData() {
      return toJS(self.data);
    },
    get hasData() {
      return self.data.length > 0;
    },
    get paginationInfo() {
      return {
        totalCount: self.totalCount,
        totalPages: self.totalPages,
        currentPage: self.currentPage,
        pageSize: self.pageSize
      };
    },
    /** Export filters; start/end dates come from loaded row timestamps, not search date pickers. */
    get exportPayload() {
      return buildRowScopedExportPayload(self.lastPayload, self.data);
    }
  }))
  .actions((self) => ({
    setPageSize(size) {
      applySnapshot(self, { ...self, pageSize: size });
    },
    setLastPayload(payload) {
      applySnapshot(self, { ...self, lastPayload: payload });
    },
    setTotalCount(count) {
      applySnapshot(self, { ...self, totalCount: count });
    },
    setTotalPages(pages) {
      applySnapshot(self, { ...self, totalPages: pages });
    },
    setCurrentPage(page) {
      applySnapshot(self, { ...self, currentPage: page });
    },
    fetchInitial(payload = {}) {
      self.resetData();

      const requestPayload = {
        ...payload,
        offset: 0,
        limit: self.pageSize
      };

      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA, {
        payload: requestPayload
      });
    },
    fetchPage(page, limit = null) {
      const pageSize = limit || self.pageSize;
      const offset = page * pageSize;

      const requestPayload = {
        ...self.lastPayload,
        offset,
        limit: pageSize
      };

      self.setPageSize(pageSize);
      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA, {
        payload: requestPayload
      });
    },
    fetch(payload) {
      if (payload?.isInitialState) {
        const initialState = [];
        applySnapshot(self.data, initialState);
        Framework.getEventManager().publish(constants.SET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA);
      }
      Framework.getEventManager().publish(constants.GET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA, {
        payload
      });
    },
    setResponse(response) {
      if (!response) {
        Framework.getEventManager().publish(constants.SET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA);
        return;
      }
      let responseData = [];
      let paginationData = null;

      if (response.status === 'success' && Array.isArray(response.data)) {
        responseData = response.data;
        paginationData = response.pagination || null;
      } else if (response.success !== undefined) {
        responseData = response.data || [];
        paginationData = response.pagination || null;
      } else if (Array.isArray(response)) {
        responseData = response;
      } else if (response.data) {
        responseData = Array.isArray(response.data.data) ? response.data.data : response.data;
        paginationData = response.pagination || null;
      }

      const enrichedData = responseData.map((item) => ({
        ...item,
        createdDate: utils.toEpochUTC(item.serverDateTime)
      }));

      applySnapshot(self.data, enrichedData);

      if (paginationData) {
        self.setTotalCount(paginationData.totalCount || 0);
        self.setTotalPages(paginationData.totalPages || 0);

        const currentPageIndex =
          paginationData.offset !== undefined && paginationData.limit
            ? Math.floor(paginationData.offset / paginationData.limit)
            : paginationData.currentPage || 0;
        self.setCurrentPage(currentPageIndex);
      } else {
        self.setTotalCount(responseData.length);
        self.setTotalPages(1);
        self.setCurrentPage(0);
      }
      Framework.getEventManager().publish(constants.SET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA);
    },
    resetData() {
      applySnapshot(self.data, []);
      self.setTotalCount(0);
      self.setTotalPages(0);
      self.setCurrentPage(0);
      self.setLastPayload({});
    },
    resetStoreValues() {
      self.resetData();
      Framework.getEventManager().publish(constants.SET_AI_CONTENT_FILTER_CHARTS_DETAILS_DATA);
    }
  }));

export default TrendsBlockedChartsDetailsModel;
